import styled from '@emotion/styled';
import { Button, Stack, tokens } from '@hotjar/hotsauce';
import { GuideCard } from '@hotjar/activation-toolkit';
import { useTranslation } from '@hotjar/translations';
import { useLocalStorage } from '@hotjar/insights-common/hooks/useStorage';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';

import { useCurrentSite } from '@hotjar/site';
import { useOptimizelyFeatureForUser } from '@hotjar/experimentation';
import { useGate } from '@hotjar/allowances';
import ImgDemo from './guide-card-img.svg';

const Img = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('${ImgDemo}');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
  background-color: #fcfae6; // this should be noon600 but it's not available yet
`;

const Wrapper = styled.div`
  margin: 0 ${tokens.spacer.x5};
`;

const RESPONSES_API_GUIDE_CARD_DISMISSED_STORAGE_KEY = 'responsesAPIGuidecardDismissed';

export const ResponseAPIGuideCard = (): JSX.Element | null => {
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-responses.api-guidecard' });
  const { data: currentSite } = useCurrentSite();

  const isFeatureEnabled = useOptimizelyFeatureForUser('sea_public_api_keys');
  const [isGuideCardDismissed, setGuideCardDismissed] = useLocalStorage<boolean>(
    RESPONSES_API_GUIDE_CARD_DISMISSED_STORAGE_KEY,
  );
  const hasFeatureGate = useGate('feedback.survey_responses_public_api', {
    id: currentSite?.id !== undefined ? String(currentSite.id) : null,
    type: 'site',
  });

  if (currentSite === undefined) return null;

  const isGuideCardVisible = isFeatureEnabled && !isGuideCardDismissed;

  const handleOnDismiss = () => {
    setGuideCardDismissed(true);
  };

  const handleUpsellClick = () => {
    upsell({ site: currentSite, referrer: 'public_api' }).catch(noop);
  };

  return isGuideCardVisible ? (
    <Wrapper>
      <GuideCard
        label={t('label')}
        title={t('title')}
        description={t('description')}
        media={<Img />}
        onDismiss={handleOnDismiss}
        actions={
          <Stack>
            <Button
              icon="open_new_window"
              kind="secondary"
              as="a"
              href="https://help.hotjar.com/hc/en-us/articles/12244109929751"
              isExternal
            >
              {t('more-info')}
            </Button>
            {hasFeatureGate !== true && (
              <Button kind="ghost" onClick={handleUpsellClick}>
                {t('compare-plans')}
              </Button>
            )}
          </Stack>
        }
      />
    </Wrapper>
  ) : null;
};
